import React, { useState, useEffect, useRef } from "react";
import DayTimetable from "../../Components/dayTimetable";
import DateSelector from "../../Components/dateSelector";
import SchoolWeekSelector from "../../Components/schoolWeekSelector";
import ClassStatsGrid from "../../Components/classStatsGrid";
import { Toggle } from "@carbon/react";
import StudentSelector from "../../Components/studentSelector";
import TeacherStudentWeekView from "../../Components/teacherStudentWeekView";
import StudentPersonalizationsView from "../../Components/studentPersonalizationsView";
import { Link, useSearchParams } from "react-router-dom";
import { gql, useMutation, useLazyQuery } from "@apollo/client";

// Lightweight query to fetch only the student's editing flag and their class' senior flag
const GET_STUDENT_EDITING = gql`
    query GetStudentEditing($studentId: String!) {
        userById(id: $studentId) {
            ... on Student {
                id
                editing
                class {
                    id
                    senior
                }
                timeplanCustomizations {
                    id
                    fromUnit
                    toUnit
                    content
                }
            }
        }
    }
`;

const SET_EDITING = gql`
    mutation SetEditing($studentId: String!, $editing: Boolean!) {
        setEditing(studentId: $studentId, canEdit: $editing)
    }
`;

export default function TeacherStudentEdit({ selectedClass }) {
    const [searchParams, setSearchParams] = useSearchParams();
    
    // Initialize from URL parameters or default values
    const weekParam = searchParams.get('week');
    const studentParam = searchParams.get('student');
    // If there's no week parameter, keep state null so the SchoolWeekSelector
    // can auto-select the current week and write it to the URL.
    let parsedWeek = null;
    if (weekParam !== null) {
        const p = parseInt(weekParam, 10);
        parsedWeek = isNaN(p) ? null : p;
    }
    const [schoolWeek, setSchoolWeek] = useState(parsedWeek);
    const [selectedStudent, setSelectedStudent] = useState(studentParam || null);
    const [editEnabled, setEditEnabled] = useState(false);

    // New: track whether the student's class is senior so we know whether to show the toggle
    const [classIsSenior, setClassIsSenior] = useState(false);

    // GraphQL hooks for setting editing and fetching lightweight student data
    // expose loading flag so we can disable the toggle while mutation is in-flight
    const [setEditingMutation, { loading: setEditingLoading }] = useMutation(SET_EDITING);
    // Include loading and error to help debug why data isn't coming back
    // Use fetchPolicy 'no-cache' and handlers so nothing is cached and results/errors are logged immediately
    const [fetchStudent, { data: studentData, loading: studentLoading, error: studentError }] = useLazyQuery(GET_STUDENT_EDITING, {
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: true
    });

    // Update URL when week changes
    const handleWeekChange = (weekNumber) => {
        setSchoolWeek(weekNumber);
        
        // Use the same pattern as class selector - simple object
        const params = { ...Object.fromEntries(searchParams) };
        params.week = weekNumber.toString();
        setSearchParams(params);
    };

    // Handle student selection changes
    const handleStudentChange = (studentId) => {
        setSelectedStudent(studentId);
        
        // Use the same pattern as class selector - simple object
        const params = { ...Object.fromEntries(searchParams) };
        if (studentId) {
            params.student = studentId;
        } else {
            delete params.student;
        }
        setSearchParams(params);
    };

    // When lightweight student data arrives, update local flags
    useEffect(() => {
        const u = studentData && studentData.userById;
        if (u) {
            const serverEditing = Boolean(u.editing);
            const serverSenior = Boolean(u.class?.senior);

            if (serverEditing !== editEnabled) {
                setEditEnabled(serverEditing);
            }

            if (serverSenior !== classIsSenior) {
                setClassIsSenior(serverSenior);
            }
        }
    }, [studentData, studentLoading, studentError]);

    // Fetch the lightweight student data when selected student changes
    useEffect(() => {
        if (selectedStudent) {
            fetchStudent({ variables: { studentId: selectedStudent }, fetchPolicy: 'no-cache' });
        } else {
            // No student selected -> reset
            setEditEnabled(false);
            setClassIsSenior(false);
        }
    }, [selectedStudent, fetchStudent]);

    // Update component state when URL changes
    useEffect(() => {
        const weekParam = searchParams.get('week');
        if (weekParam !== null) {
            const parsedWeek = parseInt(weekParam, 10);
            if (!isNaN(parsedWeek) && parsedWeek !== schoolWeek) {
                setSchoolWeek(parsedWeek);
            }
        }
        
        const studentParam = searchParams.get('student');
        if (studentParam !== selectedStudent) {
            setSelectedStudent(studentParam);
        }
    }, [searchParams]);

    const teacherWeekRefreshRef = useRef(null);

    if (!selectedClass) {
        return (
            <section className="container mx-auto">
                <p className="text-gray-600 mb-4">
                    Keine Klasse ausgewählt
                </p>
            </section>
        );
    }


    return (
        <section className="container mx-auto">
            <div className="my-6 flex justify-between items-center space-x-4">
                <div className="w-full flex items-center space-x-4 md:w-2/3">
                    <SchoolWeekSelector
                        selectedWeek={schoolWeek}
                        onWeekChange={handleWeekChange}
                    />
                    <StudentSelector
                        classId={selectedClass}
                        selectedStudent={selectedStudent}
                        onStudentChange={handleStudentChange}
                    />
                </div>
                <div className="toggle-wrapper justify-end">
                    {/* Only show the editing toggle for senior classes */}
                    {classIsSenior && selectedStudent && (
                        <Toggle
                            id="toggle-3"
                            labelText="Bearbeitung"
                            labelA={null}
                            labelB={null}
                            toggled={editEnabled}
                            disabled={setEditingLoading}
                            onToggle={() => {
                                if (!selectedStudent) return;
                                const newVal = !editEnabled;
                                // Optimistically update UI
                                setEditEnabled(newVal);
                                // Ensure boolean is explicit to avoid sending null
                                const editingValue = newVal === true;
                                // Fire mutation, ignore its result; then briefly refetch the lightweight student data
                                setEditingMutation({ variables: { studentId: selectedStudent, editing: editingValue } })
                                    .then(() => {})
                                    .catch(err => console.error('setEditing error', err))
                                    .finally(() => {
                                        // Refetch only the student's editing and class.senior to avoid overfetching
                                        fetchStudent({ variables: { studentId: selectedStudent }, fetchPolicy: 'no-cache' });
                                    });
                            }}
                        />
                    )}
                </div>
            </div>
            <div className="my-6">
                {selectedStudent && (
                    <div className="">
                        <TeacherStudentWeekView
                            weekIndex={schoolWeek}
                            studentId={selectedStudent}
                            refreshRef={teacherWeekRefreshRef}
                        />
                        <StudentPersonalizationsView studentUid={selectedStudent} onRefreshRequested={() => {
                            try { teacherWeekRefreshRef.current && teacherWeekRefreshRef.current(); } catch (e) {}
                        }} />
                    </div>
                )}
            </div>
            
        </section>
    );
}
